import api from "./api";

export interface AdminCreateGroupData {
  title: string;
  description: string;
  activityType: "friendship" | "dating" | "networking" | "events" | "other";
  privacySetting: "public" | "private" | "hidden";
  location: string;
  zipCode?: string;
  isPaid: boolean;
  membershipFee?: number;
  paymentType?: "one-time" | "recurring";
  latitude?: number;
  longitude?: number;
  paymentMethod?: string;
  expirationDate?: string;
}

export interface Group {
  _id: string;
  title: string;
  description: string;
  activityType: string;
  privacySetting: string;
  location: string;
  zipCode: string;
  isPaid: boolean;
  membershipFee?: number;
  creator: string;
  members: Array<{ _id: string; [key: string]: any }>;
  groupImageUrl?: string;
  groupVideoUrl?: string;
  createdAt: string;
  expirationDate: string;
  status: "Active" | "Expired";
}

export interface GroupsListResponse {
  groups: Group[];
  total: number;
  page: number;
  limit: number;
}

export const adminGroupsAPI = {
  // Create a group as admin
  createGroup: async (groupData: AdminCreateGroupData) => {
    console.log("🚀 ~ groupData:", JSON.stringify(groupData, null, 2));

    try {
      const response = await api.post("/groups/admin", groupData);

      console.log("🚀 ~ response:", JSON.stringify(response, null, 2));

      return {
        success: response.data.success || false,
        message: response.data.message || "Group created successfully",
        data: response.data.data,
      };
    } catch (error: any) {
      console.error("Error creating group:", error);
      throw new Error(
        error.response?.data?.message || "Failed to create group"
      );
    }
  },

  // Upload group image (reuse mobile endpoint)
  uploadGroupImage: async (groupId: string, file: File) => {
    try {
      const formData = new FormData();
      formData.append("groupImage", file);

      const response = await api.post(`/groups/${groupId}/media`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
      });

      return {
        success: response.data.success || false,
        message: response.data.message || "Group image uploaded successfully",
        data: response.data.data,
      };
    } catch (error: any) {
      console.error("Error uploading group image:", error);
      throw new Error(
        error.response?.data?.message || "Failed to upload group image"
      );
    }
  },

  // Upload group video (reuse mobile endpoint)
  uploadGroupVideo: async (groupId: string, file: File) => {
    try {
      const formData = new FormData();
      formData.append("groupVideo", file);

      const response = await api.post(`/groups/${groupId}/media`, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
          Accept: "application/json",
        },
      });

      return {
        success: response.data.success || false,
        message: response.data.message || "Group video uploaded successfully",
        data: response.data.data,
      };
    } catch (error: any) {
      console.error("Error uploading group video:", error);
      throw new Error(
        error.response?.data?.message || "Failed to upload group video"
      );
    }
  },

  // Get all groups (for admin dashboard)
  getAllGroups: async (params?: {
    page?: number;
    limit?: number;
    status?: "Active" | "Expired";
    activityType?: string;
    search?: string;
  }): Promise<GroupsListResponse> => {
    try {
      const queryParams = new URLSearchParams();

      if (params?.page) queryParams.append("page", params.page.toString());
      if (params?.limit) queryParams.append("limit", params.limit.toString());
      if (params?.status) queryParams.append("status", params.status);
      if (params?.activityType)
        queryParams.append("activityType", params.activityType);
      if (params?.search) queryParams.append("search", params.search);

      const queryString = queryParams.toString();
      const url = queryString
        ? `/groups/admin/all?${queryString}`
        : "/groups/admin/all";

      const response = await api.get(url);
      return response.data;
    } catch (error: any) {
      console.error("Error fetching groups:", error);
      throw new Error(
        error.response?.data?.message || "Failed to fetch groups"
      );
    }
  },

  // Get single group details
  getGroupById: async (groupId: string): Promise<Group> => {
    try {
      const response = await api.get(`/groups/${groupId}`);
      return response.data.data || response.data;
    } catch (error: any) {
      console.error("Error fetching group:", error);
      throw new Error(
        error.response?.data?.message || "Failed to fetch group details"
      );
    }
  },

  // Update group
  updateGroup: async (
    groupId: string,
    updateData: Partial<AdminCreateGroupData>
  ) => {
    try {
      const response = await api.patch(`/groups/admin/${groupId}`, updateData);
      return {
        success: true,
        message: response.data.message || "Group updated successfully",
        data: response.data.data || response.data,
      };
    } catch (error: any) {
      console.error("Error updating group:", error);
      throw new Error(
        error.response?.data?.message || "Failed to update group"
      );
    }
  },

  // Delete group
  deleteGroup: async (groupId: string) => {
    try {
      const response = await api.delete(`/groups/admin/${groupId}`);
      return {
        success: true,
        message: response.data.message || "Group deleted successfully",
      };
    } catch (error: any) {
      console.error("Error deleting group:", error);
      throw new Error(
        error.response?.data?.message || "Failed to delete group"
      );
    }
  },

  // Get group statistics
  getGroupStats: async () => {
    try {
      const response = await api.get("/groups/admin/stats");
      return response.data;
    } catch (error: any) {
      console.error("Error fetching stats:", error);
      throw new Error(
        error.response?.data?.message || "Failed to fetch statistics"
      );
    }
  },

  // Get reported groups
  getReportedGroups: async (params?: { page?: number; limit?: number }) => {
    try {
      const queryParams = new URLSearchParams();

      if (params?.page) queryParams.append("page", params.page.toString());
      if (params?.limit) queryParams.append("limit", params.limit.toString());

      const queryString = queryParams.toString();
      const url = queryString
        ? `/groups/admin/reported?${queryString}`
        : "/groups/admin/reported";

      const response = await api.get(url);
      return response.data;
    } catch (error: any) {
      console.error("Error fetching reported groups:", error);
      throw new Error(
        error.response?.data?.message || "Failed to fetch reported groups"
      );
    }
  },

  // Suspend/unsuspend group
  toggleGroupStatus: async (groupId: string, suspend: boolean) => {
    try {
      const endpoint = suspend ? "suspend" : "unsuspend";
      const response = await api.post(`/groups/admin/${groupId}/${endpoint}`);
      return {
        success: true,
        message:
          response.data.message ||
          `Group ${suspend ? "suspended" : "unsuspended"} successfully`,
      };
    } catch (error: any) {
      console.error("Error toggling group status:", error);
      throw new Error(
        error.response?.data?.message || "Failed to change group status"
      );
    }
  },
};
